import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';

type Organization = Tables<'organizations'>;
type OrganizationInsert = TablesInsert<'organizations'>;

export function useUserOrganization() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['organization', user?.id],
    queryFn: async () => {
      console.log('🏢 Fetching organization for user:', user?.id);
      
      if (!user?.id) {
        console.log('🏢 No user ID, returning null');
        return null;
      }
      
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('owner_id', user.id)
        .single();
        
      if (error && error.code !== 'PGRST116') {
        console.error('🏢 Error fetching organization:', error);
        throw error;
      }
      
      console.log('🏢 Organization data:', data);
      return data;
    },
    enabled: !!user?.id,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (orgData: Omit<OrganizationInsert, 'owner_id'>) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('organizations')
        .insert({
          ...orgData,
          owner_id: user.id,
        })
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', user?.id] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: Partial<Omit<Organization, 'id' | 'owner_id' | 'created_at' | 'updated_at'>>; 
    }) => {
      const { data, error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', user?.id] });
    },
  });
}